import { z } from 'zod';
import { AutomationType } from '@/shared/types';

// Automation validation (for form - config is a string)
export const automationSchema = z
  .object({
    type: z.nativeEnum(AutomationType, {
      errorMap: () => ({ message: 'Tipo de automação inválido' }),
    }),
    name: z
      .string()
      .min(3, 'Nome deve ter no mínimo 3 caracteres')
      .max(100, 'Nome deve ter no máximo 100 caracteres'),
    description: z.string().max(500, 'Descrição deve ter no máximo 500 caracteres').optional(),
    isActive: z.boolean(),
    slug: z
      .string()
      .min(3, 'Slug deve ter no mínimo 3 caracteres')
      .max(100, 'Slug deve ter no máximo 100 caracteres')
      .regex(/^[a-z0-9-]+$/, 'Slug deve conter apenas letras minúsculas, números e hífens')
      .optional(),
    schedule: z
      .string()
      .regex(
        /^(\*|[0-9]+|\*\/[0-9]+|[0-9]+-[0-9]+|[0-9]+(,[0-9]+)*)\s+(\*|[0-9]+|\*\/[0-9]+|[0-9]+-[0-9]+|[0-9]+(,[0-9]+)*)\s+(\*|[0-9]+|\*\/[0-9]+|[0-9]+-[0-9]+|[0-9]+(,[0-9]+)*)\s+(\*|[0-9]+|\*\/[0-9]+|[0-9]+-[0-9]+|[0-9]+(,[0-9]+)*)\s+(\*|[0-9]+|\*\/[0-9]+|[0-9]+-[0-9]+|[0-9]+(,[0-9]+)*)$/,
        'Formato de cron inválido. Use: *, números, */n, n-m ou listas (ex: 0 */6 * * *)'
      )
      .optional()
      .or(z.literal('')),
    jobType: z.string().min(1, 'Tipo de job é obrigatório').optional(),
    baseTimeMinutes: z.number().min(0, 'Tempo base deve ser maior ou igual a 0').optional(),
    timePerUnitMinutes: z
      .number()
      .min(0, 'Tempo por unidade deve ser maior ou igual a 0')
      .optional(),
    config: z
      .string()
      .refine(
        (val) => {
          if (!val || val.trim() === '') return true; // Empty is valid
          try {
            JSON.parse(val);
            return true;
          } catch {
            return false;
          }
        },
        { message: 'JSON inválido' }
      )
      .optional(),
  })
  .refine(
    (data) => {
      // If type is CRON, schedule is required
      if (data.type === AutomationType.CRON) {
        return !!data.schedule && data.schedule.trim() !== '';
      }
      return true;
    },
    {
      message: 'Agendamento (Schedule) é obrigatório para automações do tipo Scheduler',
      path: ['schedule'],
    }
  )
  .refine(
    (data) => {
      // If type is WEBHOOK, slug is required
      if (data.type === AutomationType.WEBHOOK) {
        return !!data.slug && data.slug.trim() !== '';
      }
      return true;
    },
    {
      message: 'Slug é obrigatório para automações do tipo Event',
      path: ['slug'],
    }
  );

// Type for API request (config is an object)
export type AutomationFormData = {
  type: AutomationType;
  name: string;
  description?: string;
  isActive: boolean;
  slug?: string;
  schedule?: string;
  config?: Record<string, unknown>;
};

// Cron presets
export const cronPresets = [
  { label: 'Todo dia às 9h', value: '0 9 * * *' },
  { label: 'A cada 6 horas', value: '0 */6 * * *' },
  { label: 'A cada 3 horas', value: '0 */3 * * *' },
  { label: 'A cada hora', value: '0 * * * *' },
  { label: 'A cada 30 minutos', value: '*/30 * * * *' },
  { label: 'A cada 15 minutos', value: '*/15 * * * *' },
  { label: 'Segunda a Sexta às 8h', value: '0 8 * * 1-5' },
  { label: 'Toda segunda às 9h', value: '0 9 * * 1' },
  { label: 'Todo domingo às 10h', value: '0 10 * * 0' },
  { label: 'Customizado', value: 'custom' },
];

// Payment validation
export const paymentSchema = z.object({
  name: z
    .string()
    .min(3, 'Nome deve ter no mínimo 3 caracteres')
    .max(100, 'Nome deve ter no máximo 100 caracteres')
    .regex(/^[a-zA-ZÀ-ÿ\s]+$/, 'Nome deve conter apenas letras e espaços'),
  email: z
    .string()
    .min(1, 'E-mail é obrigatório')
    .email('E-mail inválido')
    .max(100, 'E-mail deve ter no máximo 100 caracteres'),
  phone: z
    .string()
    .min(1, 'Telefone é obrigatório')
    .refine(
      (val) => {
        const cleaned = val.replace(/\D/g, '');
        return cleaned.length === 13 && cleaned.startsWith('55');
      },
      {
        message: 'Telefone inválido. Use o formato: +55 (11) 99999-9999',
      }
    ),
});

// Verification code validation
export const verificationCodeSchema = z.object({
  code: z
    .string()
    .min(6, 'Código deve ter 6 dígitos')
    .max(6, 'Código deve ter 6 dígitos')
    .regex(/^\d{6}$/, 'Código deve conter apenas números'),
});
